export function dateCompare(a, b) {
    // return undefined first, if applicable
    if ((a === undefined || a === null) && (b === undefined || b === null))
        return 0;
    if (a === undefined || a === null)
        return -1;
    if (b === undefined || b === null)
        return 1;
    return Math.sign(a.getTime() - b.getTime());
}
export function toStartOfDay(d) {
    return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}
export function getDateRange(start, end) {
    var current = toStartOfDay(start);
    var endMidnight = toStartOfDay(end);
    var offset = start.getTime() - current.getTime();
    var result = [];
    do {
        result.push(new Date(current.getTime() + offset));
        current.setDate(current.getDate() + 1);
    } while (current <= endMidnight);
    return result;
}
/** Determine if two date ranges overlap each other */
export function overlaps(start1, end1, start2, end2) {
    // False if: 2 starts after 1 OR 2 ends before 1 starts
    return !(start2.isAfter(end1) || end2.isBefore(start1));
}
/** Determine if two date ranges overlap each other */
export function overlapsDates(start1, end1, start2, end2) {
    // Does not overlap if one ends before the other starts
    return !(end1 < start2 || end2 < start1);
}
/**
 * Determine how close a given date is to a reference date.  Should only be
 * used when the date is >= the reference; will return `"sameDay"`, `"nextDay"`,
 * `"nextWeek"`, or `"sameElse"` (for more than a week away).
 */
export function getRelativeDateProximity(test, referenceDate) {
    // ensure every time is midnight
    var testDate = toStartOfDay(test);
    // same day
    var testSameDayReference = toStartOfDay(referenceDate);
    if (testDate <= testSameDayReference)
        return 'sameDay';
    // check day after (for tomorrow)
    var testNextDayReference = new Date(toStartOfDay(referenceDate).setDate(testSameDayReference.getDate() + 1));
    if (testDate <= testNextDayReference)
        return 'nextDay';
    // check next six days
    // does not check 7 as, for example, saying "closing Monday at 5:00"
    // is ambiguous if it currently is Monday.
    var testNextWeekReference = new Date(toStartOfDay(referenceDate).setDate(testSameDayReference.getDate() + 6));
    if (testDate <= testNextWeekReference)
        return 'nextWeek';
    return 'sameElse';
}
/** Ensure a <= b, based on months */
export function isSameUTCMonthOrBefore(a, b) {
    return ((a.getUTCFullYear() === b.getUTCFullYear() &&
        a.getUTCMonth() <= b.getUTCMonth()) ||
        a.getUTCFullYear() < b.getUTCFullYear());
}
/** Ensure a <= b, based on months */
export function isSameMonthOrBefore(a, b) {
    return ((a.getFullYear() === b.getFullYear() && a.getMonth() <= b.getMonth()) ||
        a.getFullYear() < b.getFullYear());
}
/** Ensure a == b, based on months */
export function isSameMonth(a, b) {
    return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth();
}
/** Ensure a == b, based on months */
export function isSameUTCMonth(a, b) {
    return (a.getUTCFullYear() === b.getUTCFullYear() &&
        a.getUTCMonth() === b.getUTCMonth());
}
export function dateUTCToYYYYMMDD(d) {
    return [
        d.getUTCFullYear(),
        ('0' + (d.getUTCMonth() + 1)).slice(-2),
        ('0' + d.getUTCDate()).slice(-2)
    ].join('-');
}
export function dateToYYYYMMDD(d) {
    return [
        d.getFullYear(),
        ('0' + (d.getMonth() + 1)).slice(-2),
        ('0' + d.getDate()).slice(-2)
    ].join('-');
}
export function dateFromYYYYMMDD(d) {
    var parts = d.split('-').map(function (n) { return parseInt(n, 10); });
    return new Date(parts[0], parts[1] - 1, parts[2]);
}
export function dateUTCFromYYYYMMDD(d) {
    var parts = d.split('-').map(function (n) { return parseInt(n, 10); });
    return new Date(Date.UTC(parts[0], parts[1] - 1, parts[2]));
}
export function dateFromYYYYMMDDAndHHMM(d, t) {
    var dateParts = d.split('-').map(function (n) { return parseInt(n, 10); });
    var timeParts = t.split(':').map(function (n) { return parseInt(n, 10); });
    return new Date(dateParts[0], dateParts[1] - 1, dateParts[2], timeParts[0], timeParts[1]);
}
export function dateFromDateAndHHMM(d, t) {
    var timeParts = t.split(':').map(function (n) { return parseInt(n, 10); });
    return new Date(d.getFullYear(), d.getMonth(), d.getDate(), timeParts[0], timeParts[1]);
}
export function dateFromHHMM(t) {
    var timeParts = t.split(':').map(function (n) { return parseInt(n, 10); });
    return new Date(0, 0, 0, timeParts[0], timeParts[1]);
}
export function dateUTCFromHHMM(t) {
    var timeParts = t.split(':').map(function (n) { return parseInt(n, 10); });
    return new Date(Date.UTC(0, 0, 0, timeParts[0], timeParts[1]));
}
export function dateToTimeOnly(d) {
    return new Date(0, 0, 0, d.getHours(), d.getMinutes());
}
export function minDate(dates) {
    if (dates.length === 0)
        return null;
    return new Date(Math.min.apply(Math, dates.map(function (d) { return d.getTime(); })));
}
export function maxDate(dates) {
    if (dates.length === 0)
        return null;
    return new Date(Math.max.apply(Math, dates.map(function (d) { return d.getTime(); })));
}
export function isBetweenDatesByDay(test, left, right) {
    var testStart = toStartOfDay(test);
    var leftStart = toStartOfDay(left);
    var rightStart = toStartOfDay(right);
    return leftStart <= testStart && testStart <= rightStart;
}
/** Localize time with `react-intl` */
export function getLocalizedTime(intl, time) {
    // forcibly use UTC for local time-ness
    var date = dateUTCFromHHMM(time);
    if ((date.getUTCHours() === 23 && date.getUTCMinutes() === 59) ||
        (date.getUTCHours() === 0 && date.getUTCMinutes() === 0)) {
        return intl.formatMessage({ id: 'ui-calendar.midnight' });
    }
    return intl.formatTime(date, { timeZone: 'UTC' });
}
/** Localize date with `react-intl` */
export function getLocalizedDate(intl, date) {
    return intl.formatDate(dateUTCFromYYYYMMDD(date), { timeZone: 'UTC' });
}
